import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApi } from '@backstage/frontend-plugin-api';
import {
  AssistantEvent,
  ChatMessage,
  ChoiceSet,
  Citation,
  ExecuteResponse,
  MAX_HISTORY_MESSAGES,
  MAX_MESSAGE_CHARS,
  PageContext,
  Proposal,
} from '@internal/plugin-assistant-common';
import { AssistantApi, assistantApiRef } from '../api';

/** One rendered entry. History sent to the backend is derived from it. */
export type Entry =
  | { kind: 'user'; text: string }
  | {
      kind: 'assistant';
      text: string;
      citations: Citation[];
      removedCitations: string[];
      /** What the assistant looked at on the way, in order. */
      steps?: string[];
    }
  | { kind: 'proposal'; proposal: Proposal; outcome?: ProposalOutcome }
  | { kind: 'choices'; choices: ChoiceSet }
  | { kind: 'error'; text: string };

/**
 * What became of a confirm card. Kept in the conversation, not the card, so
 * closing the panel can neither re-arm a sent card nor lose its run.
 */
export type ProposalOutcome = { result?: ExecuteResponse; dismissed?: boolean };

/** Last MAX_HISTORY_MESSAGES chat turns; proposals and errors are UI only. */
export function toHistory(entries: Entry[]): ChatMessage[] {
  return entries
    .flatMap((e): ChatMessage[] => {
      if (e.kind === 'user' || e.kind === 'assistant') {
        return [{ role: e.kind, content: e.text.slice(0, MAX_MESSAGE_CHARS) }];
      }
      return [];
    })
    .slice(-MAX_HISTORY_MESSAGES);
}

export const TOOL_LABELS: Record<string, string> = {
  whoami: 'Checking who you are',
  list_offerings: 'Reading offerings',
  get_offering: 'Reading an offering',
  list_environments: 'Reading your environments',
  get_environment: 'Reading an environment',
  list_runs: 'Reading runs',
  get_run: 'Reading a run',
  list_application_groups: 'Reading your Application Groups',
  get_order_form: 'Reading the order form',
  list_destinations: 'Reading destinations',
  list_destination_choices: 'Reading the choices',
  order_form_link: 'Preparing a form link',
  catalog_search: 'Searching the catalog',
  catalog_get: 'Reading a catalog entry',
  docs_search: 'Searching the user guide',
  docs_get: 'Reading the user guide',
  offer_choices: 'Preparing your choices',
  propose_create_environment: 'Preparing a proposal',
  propose_destroy_environment: 'Preparing a proposal',
};

export type Conversation = {
  entries: Entry[];
  busy: boolean;
  status?: string;
  send(text: string, page?: PageContext): Promise<void>;
  stop(): void;
  reset(): void;
  settle(proposalId: string, outcome: ProposalOutcome): void;
};

const STORAGE_KEY = 'assistant.conversation';

/**
 * Restore a conversation after a reload. Proposals are dropped: a confirm
 * card must never come back armed, or one order could be sent twice.
 */
function restore(): Entry[] {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as Entry[]) : [];
    return Array.isArray(parsed)
      ? parsed.filter(e => e && (e.kind !== 'proposal' || e.outcome))
      : [];
  } catch {
    return [];
  }
}

function useConversationState(
  api: AssistantApi,
  persist: boolean,
): Conversation {
  const [entries, setEntries] = useState<Entry[]>(() =>
    persist ? restore() : [],
  );
  const [status, setStatus] = useState<string>();
  const [busy, setBusy] = useState(false);
  const abort = useRef<AbortController>();
  // Proposals arrive before the answer that explains them; show the
  // explanation first, then the card to confirm.
  const pending = useRef<Entry[]>([]);
  const steps = useRef<string[]>([]);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  useEffect(() => () => abort.current?.abort(), []);
  useEffect(() => {
    if (!persist) return;
    try {
      window.sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          entries.filter(e => e.kind !== 'proposal' || e.outcome).slice(-40),
        ),
      );
    } catch {
      // Storage full or blocked: the conversation still works in memory.
    }
  }, [entries, persist]);

  const onEvent = useCallback((event: AssistantEvent) => {
    switch (event.type) {
      case 'status': {
        const label = TOOL_LABELS[event.tool] ?? 'Working';
        setStatus(label);
        if (!steps.current.includes(label)) steps.current.push(label);
        break;
      }
      case 'proposal':
        pending.current.push({ kind: 'proposal', proposal: event.proposal });
        break;
      case 'choices':
        pending.current.push({ kind: 'choices', choices: event.choices });
        break;
      case 'answer': {
        const proposals = pending.current;
        pending.current = [];
        const used = steps.current;
        steps.current = [];
        setEntries(e => [
          ...e,
          {
            kind: 'assistant',
            text: event.text,
            citations: event.citations,
            removedCitations: event.removedCitations,
            steps: used,
          },
          ...proposals,
        ]);
        break;
      }
      case 'error':
        setEntries(e => [...e, { kind: 'error', text: event.message }]);
        break;
      default:
        break;
    }
  }, []);

  const send = useCallback(
    async (text: string, page?: PageContext) => {
      const message = text.trim();
      if (!message || abort.current) return;
      const next: Entry[] = [
        ...entriesRef.current,
        { kind: 'user', text: message },
      ];
      setEntries(next);
      setBusy(true);
      setStatus('Thinking');
      steps.current = [];
      const controller = new AbortController();
      abort.current = controller;
      try {
        await api.chat(toHistory(next), onEvent, controller.signal, page);
      } catch (error) {
        if (!controller.signal.aborted) {
          onEvent({ type: 'error', message: `Request failed: ${error}` });
        }
      } finally {
        const leftover = pending.current;
        pending.current = [];
        if (leftover.length) setEntries(e => [...e, ...leftover]);
        abort.current = undefined;
        setBusy(false);
        setStatus(undefined);
      }
    },
    [api, onEvent],
  );

  const stop = useCallback(() => abort.current?.abort(), []);

  const settle = useCallback(
    (proposalId: string, outcome: ProposalOutcome) =>
      setEntries(es =>
        es.map(e =>
          e.kind === 'proposal' && e.proposal.id === proposalId
            ? { ...e, outcome }
            : e,
        ),
      ),
    [],
  );

  const reset = useCallback(() => {
    abort.current?.abort();
    pending.current = [];
    setEntries([]);
  }, []);

  return useMemo(
    () => ({ entries, busy, status, send, stop, reset, settle }),
    [entries, busy, status, send, stop, reset, settle],
  );
}

const ConversationContext = createContext<Conversation | undefined>(undefined);

/** One conversation for the whole app, so it follows the user across pages. */
export const ConversationProvider = ({ children }: { children: ReactNode }) => {
  const api = useApi(assistantApiRef);
  const conversation = useConversationState(api, true);
  return (
    <ConversationContext.Provider value={conversation}>
      {children}
    </ConversationContext.Provider>
  );
};

/** The app-wide conversation, or a local one outside the provider. */
export function useConversation(): Conversation {
  const shared = useContext(ConversationContext);
  const local = useConversationState(useApi(assistantApiRef), false);
  return shared ?? local;
}
